import React, { useCallback, useEffect, useState, RefObject } from 'react';

interface SoundControls {
  /** A boolean indicating if the video is muted. */
  isMuted: boolean;
  /** handleMuteToggle: A function to toggle the mute state. */
  handleMuteToggle: () => void;
  /** A number representing the current volume level (0 to 1). */
  volume: number;
  /** A function to change the volume level. */
  handleVolumeChange: (newVolume: number) => void;
}

/**
 * A custom hook to manage sound controls (mute and volume) for a video element.
 * @param videoRef - A reference to the video element.
 */
export const useVideoSoundControls = (videoRef: React.RefObject<HTMLVideoElement>): SoundControls => {
  const [isMuted, setIsMuted] = useState(videoRef.current?.muted);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    const video = videoRef?.current;
    if (video) {
      if (typeof isMuted === 'undefined') {
        setIsMuted(video?.muted);
      }

      videoRef.current.muted = isMuted;
      videoRef.current.volume = volume;
    }
  }, [isMuted, volume, videoRef]);

  const handleMuteToggle = useCallback(() => {
    setIsMuted((prevMuted) => !prevMuted);
  }, []);

  const handleVolumeChange = useCallback((newVolume: number) => {
    setVolume(newVolume);
  }, []);

  return {
    isMuted,
    handleMuteToggle,
    volume,
    handleVolumeChange,
  };
};

interface PlayPause {
  /** A boolean indicating if the video is currently playing. */
  isPlaying: boolean;
  /** A function to toggle between play and pause states. */
  handlePlayPause: () => void;
}

/**
 * A custom hook to manage play/pause functionality for a video element.
 * @param videoRef - A reference to the video element.
 */
export const useVideoPlayPause = (videoRef: React.RefObject<HTMLVideoElement>): PlayPause => {
  const [isPlaying, setIsPlaying] = useState(!videoRef.current?.paused);

  useEffect(() => {
    const video = videoRef?.current;

    if (video && typeof isPlaying === 'undefined') {
      setIsPlaying(!video?.paused);
    }

    const handlePlay = () => {
      // prevent playing state when autoplay triggers play event on re-entering viewport after video ended
      !(video.autoplay && video.ended) && setIsPlaying(true);
    };
    const handlePause = () => setIsPlaying(false);

    if (video) {
      video.addEventListener('pause', handlePause);

      // Set initial state (not enough without event listeners due to autoPlay and paused race conditions)
      setIsPlaying(!video.paused);

      video.addEventListener('play', handlePlay);

      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
      };
    }
  }, [videoRef]);

  const handlePlayPause = useCallback(() => {
    if (videoRef?.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
      setIsPlaying((prevIsPlaying) => !prevIsPlaying);
    }
  }, [isPlaying, videoRef?.current]);

  return {
    isPlaying,
    handlePlayPause,
  };
};

interface VideoTime {
  /** The current playback time. */
  currentTime: number;
  /** The total duration of the video. */
  duration: number;
  /** A function to seek to a specific time. */
  handleSeek: (newTime: number) => void;
}

/**
 * useVideoTime is a custom React hook designed to manage and synchronize the playback time state
 * of a video element with the React component state. It provides an interface to track the current
 * playback time, the total duration of the video, and a method to seek to a specific time in the video.
 * @param {RefObject<HTMLVideoElement>} videoRef - A reference to the HTML video element.
 */

export const useVideoTime = (videoRef: RefObject<HTMLVideoElement>): VideoTime => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(videoRef.current?.duration);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current?.currentTime) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, [videoRef]);

  const handleSeek = useCallback(
    (newTime: number) => {
      if (videoRef.current?.currentTime) {
        videoRef.current.currentTime = newTime;
      }
      setCurrentTime(newTime);
    },
    [videoRef],
  );

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current?.duration) {
      setDuration(videoRef.current.duration);
    }
  }, [videoRef]);

  useEffect(() => {
    const video = videoRef?.current;
    if (video) {
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('timeupdate', handleTimeUpdate);

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, [handleLoadedMetadata, handleTimeUpdate, videoRef]);

  return {
    currentTime,
    duration,
    handleSeek,
  };
};
